/**
 * Helper functions for question/form field logic
 */

export interface QuestionLike {
  type: string;
  label?: string;
  triggersMultiplicity?: boolean;
}

/**
 * Determines if a question field should trigger item multiplicity.
 *
 * When a multiplicity question has a value > 1, it causes other questions
 * to be duplicated for each item instance.
 *
 * @param field - The question field to check
 * @returns true if this field should drive item multiplicity
 *
 * Logic:
 * - If `triggersMultiplicity` is explicitly true, returns true
 * - If `triggersMultiplicity` is explicitly false, returns false
 * - If `triggersMultiplicity` is undefined (legacy data), falls back to label heuristic
 *   for backward compatibility with existing questions
 */
export const isMultiplicityQuestion = (field: QuestionLike): boolean => {
  // Explicit flag takes precedence
  if (field.triggersMultiplicity === true) return true;
  if (field.triggersMultiplicity === false) return false;

  // Fallback to heuristic for backward compatibility (undefined flag)
  if (field.type !== 'number' && field.type !== 'plusminus') return false;
  const label = field.label?.toLowerCase() || '';
  return label.includes('how many') || label.includes('number of') || label.includes('quantity');
};
